<template lang="pug">
.list-block-editor
  .editor-header
    h3 {{ isCreating ? 'New List' : 'List' }}

  .editor-form
    el-form(:model="formData" label-position="top" :rules="rules" ref="formRef")
      el-form-item(label="Title" prop="title")
        el-input(
          v-model="formData.title"
          placeholder="Enter block title"
          size="default"
        )

      el-form-item(label="Organizations" prop="organizationsList")
        .reorder-actions
          .reorder-hint(v-if="formData.organizationsList.length > 1")
            span Drag to reorder organizations
          el-button(
            type="primary"
            size="small"
            @click="showOrganizationSelector = true"
            plain
          ) Add Organizations
        .organizations-list

          .organizations-list-content(v-if="formData.organizationsList.length > 0")
            draggable.selected-organizations-list(
              v-model="formData.organizationsList"
              handle=".drag-handle"
              ghost-class="ghost-organization"
              item-key="id"
              @start="dragStart"
              @end="dragEnd"
            )
              template(#item="{ element: organizationId, index }")
                .selected-organization-item(:key="organizationId")
                  .selected-organization-content
                    .drag-handle.cursor-move
                      fai(type="fas" icon="grip-vertical").size-14
                    .selected-organization-thumbnail
                      img(
                        v-if="getOrganizationAvatar(organizationId)"
                        :src="getOrganizationAvatar(organizationId)"
                      )
                      .no-image(v-else)
                    .selected-organization-info
                      .selected-organization-title
                        | {{ getOrganizationName(organizationId) }}
                  .selected-organization-actions
                    el-button(
                      type="danger"
                      size="small"
                      text
                      @click="removeOrganization(index)"
                    ).has-fai
                      fai(type="fal" icon="trash").size-14

          .organizations-list-empty(v-else)
            span No organizations selected. Click "Add Organizations" to select organizations.

      .editor-actions
        el-button(
          @click="handleCancel"
          size="default"
        ) Cancel
        el-button(
          type="primary"
          @click="handleSave"
          size="default"
          :loading="saving"
        ) Save

  // Organization selector dialog
  DialogSelectOrganizations(
    v-model="showOrganizationSelector"
    :preSelectedOrganizations="formData.organizationsList"
    @confirm="handleOrganizationsSelected"
  )
</template>

<script>
import {
  defineComponent, ref, reactive, computed, watch,
} from 'vue';
import { ElMessage } from 'element-plus';
import draggable from 'vuedraggable';
import useStaticPageUtils from '../shared/index.ts';
import DialogSelectOrganizations from './DialogSelectOrganizations.vue';

export default defineComponent({
  name: 'ListBlock',
  components: { DialogSelectOrganizations, draggable },
  props: {
    block: {
      type: Object,
      default: null,
    },
    isCreating: {
      type: Boolean,
      default: false,
    },
    pageId: {
      type: String,
      required: true,
    },
    staticPageData: {
      type: Object,
      default: null,
    },
  },
  emits: ['save', 'cancel'],
  setup(props, { emit }) {
    const { createOrUpdateListBlock } = useStaticPageUtils();
    const formRef = ref(null);
    const saving = ref(false);
    const showOrganizationSelector = ref(false);
    
    // Store organizations selected from dialog that might not be in staticPageData yet
    const dialogSelectedOrganizations = ref([]);

    // Form data
    const formData = reactive({
      title: props.block?.details?.title || '',
      organizationsList: props.block?.details?.organizationsList || [],
    });

    // Form validation rules
    const rules = {
      title: [
        { required: true, message: 'Please enter block title', trigger: 'blur' },
      ],
      organizationsList: [
        {
          type: 'array',
          required: true,
          message: 'Please select at least one organization',
          trigger: 'change',
          validator: (rule, value, callback) => {
            if (value.length === 0) {
              callback(new Error('Please select at least one organization'));
            } else {
              callback();
            }
          },
        },
      ],
    };

    // Combine organizations from static page data and dialog selections
    const organizations = computed(() => {
      const staticPageOrganizations = props.staticPageData?.organizations || [];
      const allOrganizations = [...staticPageOrganizations];
      
      // Add organizations from dialog that aren't already in staticPageData
      dialogSelectedOrganizations.value.forEach((dialogOrganization) => {
        if (!allOrganizations.find((o) => o.id === dialogOrganization.id)) {
          allOrganizations.push(dialogOrganization);
        }
      });
      
      return allOrganizations;
    });

    // Watch for changes to the block prop and reset form data
    watch(() => props.block, (newBlock) => {
      if (newBlock) {
        formData.title = newBlock.details?.title || '';
        formData.organizationsList = newBlock.details?.organizationsList || [];
      } else {
        // Reset form data when no block is selected (creating new block)
        formData.title = '';
        formData.organizationsList = [];
      }
    }, { immediate: true, deep: true });

    // Watch for changes to isCreating prop
    watch(() => props.isCreating, (isCreating) => {
      if (isCreating) {
        // Reset form data when creating a new block
        formData.title = '';
        formData.organizationsList = [];
      }
    });

    // Get organization name by ID
    function getOrganizationName(organizationId) {
      const organization = organizations.value.find((o) => o.id === organizationId);
      return organization ? organization.name : '';
    }

    // Get organization avatar by ID
    function getOrganizationAvatar(organizationId) {
      const organization = organizations.value.find((o) => o.id === organizationId);
      return organization ? organization.avatar : null;
    }

    // We're only showing active organizations, so no need for status function

    // Handle organizations selected from dialog
    function handleOrganizationsSelected(selectedIds, selectedOrganizationObjects = []) {
      formData.organizationsList = selectedIds;
      // Store the full organization objects for display
      dialogSelectedOrganizations.value = selectedOrganizationObjects;
    }

    // Remove organization from list
    function removeOrganization(index) {
      const removedOrganizationId = formData.organizationsList[index];
      formData.organizationsList.splice(index, 1);
      
      // Also remove from dialog selected organizations if it exists there
      const dialogIndex = dialogSelectedOrganizations.value.findIndex((o) => o.id === removedOrganizationId);
      if (dialogIndex !== -1) {
        dialogSelectedOrganizations.value.splice(dialogIndex, 1);
      }
    }

    // Drag event handlers
    function dragStart() {
      // Optional: Add any logic needed when dragging starts
    }

    function dragEnd() {
      // Optional: Add any logic needed when dragging ends
    }

    // Handle save
    async function handleSave() {
      if (!formRef.value) return;

      try {
        await formRef.value.validate();
        saving.value = true;

        const input = {
          title: formData.title,
          organizationsList: formData.organizationsList,
          sortOrder: props.isCreating ? null : (props.block?.sortOrder || 0),
          parentId: props.pageId,
          id: props.isCreating ? null : props.block?.id,
        };

        const result = await createOrUpdateListBlock(input, props.staticPageData);

        ElMessage.success(props.isCreating ? 'List block created successfully' : 'List block updated successfully');
        emit('save', result);
      } catch (error) {
        console.error('Error saving list block:', error);
        ElMessage.error('Failed to save list block');
      } finally {
        saving.value = false;
      }
    }

    // Handle cancel
    function handleCancel() {
      emit('cancel');
    }

    return {
      formRef,
      formData,
      rules,
      saving,
      organizations,
      dialogSelectedOrganizations,
      showOrganizationSelector,
      getOrganizationName,
      getOrganizationAvatar,
      handleOrganizationsSelected,
      removeOrganization,
      handleSave,
      handleCancel,
      dragStart,
      dragEnd,
    };
  },
});
</script>

<style scoped>
.list-block-editor {
  width: 100%;
}

.editor-form {
  max-width: 500px;
  margin-top: 20px;
}

.reorder-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.organizations-list {
  border: 1px solid #ebeef5;
  border-radius: 4px;
  padding: 12px;
}

.reorder-hint {
  display: flex;
  align-items: center;
  font-size: 12px;
  color: #909399;
}

.organizations-list-content {
  max-height: 300px;
  overflow-y: auto;
}

.selected-organizations-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.selected-organization-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  background-color: #f9f9f9;
}

.selected-organization-content {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0; /* Allow flex items to shrink below their content size */
}

.drag-handle {
  cursor: move; /* fallback */
  cursor: grab;
  color: #909399;
  margin-right: 4px;
  display: flex;
  align-items: center;
}

.drag-handle:hover {
  color: #1EBEAF;
}

.drag-handle:active {
  cursor: grabbing;
}

.ghost-organization {
  opacity: 0.5;
  background: #e6f7f6;
  border: 1px dashed #1EBEAF;
}

.selected-organization-thumbnail {
  width: 40px;
  height: 40px;
  border-radius: 4px;
  overflow: hidden;
  flex-shrink: 0;
}

.selected-organization-thumbnail img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.no-image {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #e0e0e0;
  color: #606266;
  font-size: 12px;
  border: 1px solid #d4d4d4;
}

.selected-organization-info {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  max-width: calc(100% - 120px); /* Reserve space for actions */
}

.selected-organization-title {
  font-weight: bold;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.selected-organization-status {
  font-size: 0.85em;
  color: #606266;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.selected-organization-actions {
  display: flex;
  align-items: center;
  flex-shrink: 0; /* Prevent actions from shrinking */
  margin-left: 8px;
}

.organizations-list-empty {
  padding: 20px 0;
  text-align: center;
  color: #909399;
}

.editor-actions {
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid #ebeef5;
  display: flex;
  gap: 12px;
  justify-content: flex-end;
}
</style>
